import React from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, MapPin, Heart, Briefcase, Car, Home, GraduationCap } from 'lucide-react';

export default function UseCases() {
  const useCases = [
    {
      icon: <MapPin className="h-12 w-12 text-blue-600" />,
      title: "Lost & Found Items",
      description: "Attach QR codes to valuable items. If lost, finders can message you directly without seeing your contact info.",
      example: "Perfect for luggage, keys, wallets, electronics, and personal belongings."
    },
    {
      icon: <Car className="h-12 w-12 text-green-600" />,
      title: "Vehicle Communication",
      description: "Place QR codes on cars, bikes, or motorcycles. Get notified about parking issues, accidents, or compliments.",
      example: "Great for parking violations, accident reports, or general vehicle feedback."
    },
    {
      icon: <Home className="h-12 w-12 text-purple-600" />,
      title: "Property & Real Estate",
      description: "Let potential buyers or renters contact you anonymously about properties without revealing your phone number.",
      example: "Perfect for 'For Sale' signs, rental properties, and open house events."
    },
    {
      icon: <Briefcase className="h-12 w-12 text-yellow-600" />,
      title: "Business Feedback",
      description: "Collect anonymous customer feedback, complaints, or suggestions for your business or service.",
      example: "Ideal for restaurants, retail stores, service providers, and events."
    },
    {
      icon: <Heart className="h-12 w-12 text-red-600" />,
      title: "Personal Safety",
      description: "Share QR codes for emergency contacts, medical information, or safety check-ins while maintaining privacy.",
      example: "Useful for elderly care, medical alerts, and personal emergency contacts."
    },
    {
      icon: <GraduationCap className="h-12 w-12 text-indigo-600" />,
      title: "Education & Events",
      description: "Enable anonymous communication for students, conference attendees, or event participants.",
      example: "Perfect for anonymous questions, feedback, and networking at events."
    },
    {
      icon: <QrCodeIcon className="h-12 w-12 text-teal-600" />,
      title: "Pet Identification",
      description: "Tag your pets with QR codes on collars. If they wander off, finders can contact you without exposing personal details.",
      example: "Safer than traditional tags - no address or phone number visible to strangers."
    },
    {
      icon: <Briefcase className="h-12 w-12 text-orange-600" />,
      title: "Workplace Communication",
      description: "Enable anonymous feedback, suggestions, or reporting in workplace settings while maintaining confidentiality.",
      example: "Perfect for HR feedback, safety concerns, or anonymous tip lines."
    },
    {
      icon: <Home className="h-12 w-12 text-pink-600" />,
      title: "Neighbor Communication",
      description: "Place QR codes on gates, mailboxes, or doors for neighbors to reach you about packages, noise, or community matters.",
      example: "Great for apartment buildings, gated communities, and neighborhood watch programs."
    },
    {
      icon: <Car className="h-12 w-12 text-cyan-600" />,
      title: "Service Provider Tags",
      description: "Contractors, cleaners, or service workers can leave QR codes for clients to provide feedback or report issues.",
      example: "Useful for plumbers, electricians, house cleaners, and maintenance services."
    },
    {
      icon: <Heart className="h-12 w-12 text-rose-600" />,
      title: "Dating & Networking",
      description: "Share QR codes at social events for potential connections without immediately exchanging phone numbers.",
      example: "Perfect for speed dating, networking events, and professional meetups."
    },
    {
      icon: <MapPin className="h-12 w-12 text-emerald-600" />,
      title: "Delivery & Package Management",
      description: "Attach QR codes to packages or delivery boxes for recipients to report issues or provide delivery feedback.",
      example: "Ideal for e-commerce businesses, subscription boxes, and mail-order services."
    },
    {
      icon: <GraduationCap className="h-12 w-12 text-violet-600" />,
      title: "Student ID & Campus Safety",
      description: "Students can carry QR codes for emergency contacts, dorm access, or anonymous reporting of campus issues.",
      example: "Perfect for university IDs, library cards, and student emergency systems."
    },
    {
      icon: <Briefcase className="h-12 w-12 text-amber-600" />,
      title: "Pop-up Shops & Markets",
      description: "Vendors at farmers markets or pop-up events can use QR codes for customer feedback and future contact.",
      example: "Great for artisan markets, food trucks, and temporary retail locations."
    },
    {
      icon: <Home className="h-12 w-12 text-sky-600" />,
      title: "Vacation Rental Communication",
      description: "Airbnb hosts and vacation rental owners can provide QR codes for guests to report issues or ask questions.",
      example: "Perfect for check-in instructions, property issues, and guest feedback."
    },
    {
      icon: <Heart className="h-12 w-12 text-fuchsia-600" />,
      title: "Healthcare & Medical Alerts",
      description: "Medical professionals or patients can use QR codes for appointment feedback, prescription info, or emergency contacts.",
      example: "Useful for medical ID bracelets, prescription bottles, and patient feedback."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Endless Possibilities
            <span className="block text-yellow-300">Real Use Cases</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Discover how people use iComment.app for anonymous communication in real-world scenarios.
          </p>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="mb-6">{useCase.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{useCase.description}</p>
                <p className="text-sm text-blue-600 font-medium">{useCase.example}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Success Stories</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
              <p className="text-gray-600 italic mb-4">
                "I put QR codes on my bike and camera gear. When my bike was found after being stolen, 
                the person was able to contact me immediately without any hassle!"
              </p>
              <p className="font-semibold text-gray-900">- Sarah M., Photographer</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-green-600">
              <p className="text-gray-600 italic mb-4">
                "We use iComment.app for our food truck. Customers can give us feedback anonymously, 
                and we've improved our service based on their honest comments."
              </p>
              <p className="font-semibold text-gray-900">- Mike T., Food Truck Owner</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-purple-600">
              <p className="text-gray-600 italic mb-4">
                "My dog slipped his collar at the park. The family who found him scanned the QR code and 
                I had him back within 30 minutes. No need to share my address on his tag!"
              </p>
              <p className="font-semibold text-gray-900">- Jennifer L., Pet Owner</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-orange-600">
              <p className="text-gray-600 italic mb-4">
                "As a vacation rental host, I put QR codes in each room. Guests can report issues instantly 
                and anonymously. It's improved our ratings significantly!"
              </p>
              <p className="font-semibold text-gray-900">- David R., Airbnb Superhost</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-red-600">
              <p className="text-gray-600 italic mb-4">
                "My elderly mother has a QR code bracelet with emergency contacts. It gives us peace of mind 
                knowing anyone can reach us if needed, without compromising her privacy."
              </p>
              <p className="font-semibold text-gray-900">- Maria C., Caregiver</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <p className="text-gray-600 italic mb-4">
                "I'm a contractor and leave QR codes at job sites. Clients can message me about any issues 
                after I leave. It's helped me build trust and get more referrals!"
              </p>
              <p className="font-semibold text-gray-900">- Tom H., General Contractor</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-pink-600">
              <p className="text-gray-600 italic mb-4">
                "At our university, students use QR codes for anonymous reporting of safety concerns. 
                It's created a more secure campus environment for everyone."
              </p>
              <p className="font-semibold text-gray-900">- Dr. Amanda P., Campus Safety Director</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-indigo-600">
              <p className="text-gray-600 italic mb-4">
                "I put QR codes on my car windshield. Got a message about my parking job and was able to 
                fix it before getting towed. Also received compliments on my paint job!"
              </p>
              <p className="font-semibold text-gray-900">- Alex K., Car Enthusiast</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-yellow-600">
              <p className="text-gray-600 italic mb-4">
                "We sell at farmers markets and use QR codes for customer feedback. People love being able 
                to reach us after trying our products. Sales increased 40% from repeat customers!"
              </p>
              <p className="font-semibold text-gray-900">- Rachel & Ben, Artisan Soap Makers</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Try It Yourself?</h2>
          <p className="text-xl mb-8">Start your own anonymous communication journey today.</p>
          <Link 
            to="/auth" 
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Create Your First QR Code
          </Link>
        </div>
      </section>
    </div>
  );
}